import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
    ArrowLeft,
    Copy,
    Check,
    Sparkles,
    Loader2,
    RotateCcw,
    Upload,
    Download,
    Image as ImageIcon,
} from "lucide-react";
import { fetchTool, generate, generateImage } from "../lib/api";
import { getCategoryStyle } from "../lib/categoryStyle";
import ModelSelector from "../components/ModelSelector";
import ToolArticle from "../components/ToolArticle";
import RelatedTools from "../components/RelatedTools";
import RelatedToolsStrip from "../components/RelatedToolsStrip";
import Seo from "../components/Seo";
import { toast } from "sonner";

export default function ToolPage() {
    const { slug } = useParams();
    const [tool, setTool] = useState(null);
    const [values, setValues] = useState({});
    const [modelCfg, setModelCfg] = useState({
        provider: "gemini",
        model: "gemini-2.5-flash",
    });
    const [output, setOutput] = useState("");
    const [images, setImages] = useState([]); // data URLs
    const [loading, setLoading] = useState(false);
    const [copied, setCopied] = useState(false);
    const fileInputRef = useRef(null);

    const isImageTool = tool?.kind === "image" || tool?.kind === "image_edit";
    const needsUpload = tool?.kind === "image_edit";

    useEffect(() => {
        (async () => {
            const t = await fetchTool(slug);
            setTool(t);
            const initial = {};
            (t.inputs || []).forEach((i) => (initial[i.key] = ""));
            setValues(initial);
            setOutput("");
            setImages([]);
        })();
    }, [slug]);

    const style = useMemo(
        () => getCategoryStyle(tool?.category_color || "primary"),
        [tool]
    );

    const canSubmit = useMemo(() => {
        if (!tool) return false;
        return tool.inputs.every(
            (i) => i.optional || (values[i.key] || "").trim().length > 0
        );
    }, [tool, values]);

    const onGenerate = async (e) => {
        e?.preventDefault();
        if (!tool || !canSubmit || loading) return;
        setLoading(true);
        setOutput("");
        setImages([]);
        try {
            if (isImageTool) {
                const res = await generateImage({
                    tool_slug: tool.slug,
                    inputs: values,
                    model: "gemini-3.1-flash-image-preview",
                });
                setImages(res.images || []);
                if (res.text) setOutput(res.text);
            } else {
                const res = await generate({
                    tool_slug: tool.slug,
                    inputs: values,
                    provider: modelCfg.provider,
                    model: modelCfg.model,
                });
                setOutput(res.output);
            }
        } catch (err) {
            const msg = err?.response?.data?.detail || "Generation failed";
            toast.error(msg);
        } finally {
            setLoading(false);
        }
    };

    const onCopy = async () => {
        if (!output) return;
        await navigator.clipboard.writeText(output);
        setCopied(true);
        toast.success("Copied to clipboard");
        setTimeout(() => setCopied(false), 1200);
    };

    const onReset = () => {
        const initial = {};
        (tool?.inputs || []).forEach((i) => (initial[i.key] = ""));
        setValues(initial);
        setOutput("");
        setImages([]);
    };

    const onFilePick = async (e, key) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 8 * 1024 * 1024) {
            toast.error("Image too large (max 8 MB)");
            return;
        }
        const reader = new FileReader();
        reader.onload = () => {
            setValues((v) => ({ ...v, [key]: reader.result })); // data URL
        };
        reader.readAsDataURL(file);
    };

    const downloadImage = (dataUrl, idx) => {
        const a = document.createElement("a");
        a.href = dataUrl;
        a.download = `${tool.slug}-${idx + 1}.png`;
        document.body.appendChild(a);
        a.click();
        a.remove();
    };

    if (!tool) {
        return (
            <div className="max-w-7xl mx-auto px-5 md:px-10 pt-20">
                <div className="nb-border rounded-2xl bg-white p-10 text-center">
                    Loading tool…
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-7xl mx-auto px-5 md:px-10 pt-10 pb-16">
            <Seo
                path={`/tool/${tool.slug}`}
                title={`${tool.name} — Free Online AI Tool (No Sign-up)`}
                description={`${tool.description} Free, unlimited, powered by GPT-5.2, Gemini 3, and Claude Sonnet 4.5. No login required.`}
            />
            <Link
                to={`/category/${tool.category}`}
                data-testid="back-to-category"
                className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-widest hover:underline underline-offset-4 decoration-2"
            >
                <ArrowLeft className="w-4 h-4" strokeWidth={3} />
                {tool.category_name}
            </Link>

            <header
                className={`mt-5 nb-border-4 nb-shadow rounded-3xl ${style.bg} p-6 md:p-10`}
            >
                <div className="text-xs font-bold uppercase tracking-widest text-black/70">
                    {tool.category_name}
                </div>
                <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-black tracking-tighter mt-2">
                    {tool.name}
                </h1>
                <p className="mt-3 text-lg font-medium max-w-3xl">{tool.description}</p>
                <RelatedToolsStrip
                    currentSlug={tool.slug}
                    category={tool.category}
                />
            </header>

            <div className="mt-8 grid lg:grid-cols-2 gap-6 md:gap-8">
                {/* INPUTS */}
                <form
                    onSubmit={onGenerate}
                    className="nb-border-4 rounded-3xl bg-white p-6 md:p-8 nb-shadow"
                >
                    <div className="font-display text-xl font-black mb-4 flex items-center gap-2">
                        <span className="bg-[#FDE047] nb-border px-2 py-0.5 rounded-lg text-sm">
                            Inputs
                        </span>
                    </div>

                    <div className="space-y-4">
                        {tool.inputs.map((i) => (
                            <div key={i.key}>
                                <label className="block text-xs font-bold uppercase tracking-widest mb-1.5">
                                    {i.label}
                                    {i.optional && (
                                        <span className="text-neutral-500 normal-case font-semibold ml-2">
                                            (optional)
                                        </span>
                                    )}
                                </label>

                                {i.type === "image" ? (
                                    <div className="space-y-2">
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            accept="image/*"
                                            onChange={(e) => onFilePick(e, i.key)}
                                            data-testid={`input-${i.key}`}
                                            className="hidden"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => fileInputRef.current?.click()}
                                            className="inline-flex items-center gap-2 bg-neutral-50 nb-border rounded-xl px-4 py-3 font-bold w-full hover:bg-white"
                                        >
                                            <Upload className="w-4 h-4" strokeWidth={3} />
                                            {values[i.key] ? "Replace image" : "Choose image"}
                                        </button>
                                        {values[i.key] && (
                                            <img
                                                src={values[i.key]}
                                                alt="preview"
                                                className="w-full max-h-48 object-contain nb-border rounded-xl bg-neutral-50"
                                            />
                                        )}
                                    </div>
                                ) : i.type === "textarea" ? (
                                    <textarea
                                        data-testid={`input-${i.key}`}
                                        value={values[i.key] || ""}
                                        onChange={(e) =>
                                            setValues({ ...values, [i.key]: e.target.value })
                                        }
                                        placeholder={i.placeholder}
                                        rows={5}
                                        className="w-full bg-neutral-50 nb-border rounded-xl px-4 py-3 font-medium focus:outline-none focus:bg-white focus:nb-shadow-sm resize-none"
                                    />
                                ) : (
                                    <input
                                        data-testid={`input-${i.key}`}
                                        value={values[i.key] || ""}
                                        onChange={(e) =>
                                            setValues({ ...values, [i.key]: e.target.value })
                                        }
                                        placeholder={i.placeholder}
                                        className="w-full bg-neutral-50 nb-border rounded-xl px-4 py-3 font-medium focus:outline-none focus:bg-white focus:nb-shadow-sm"
                                    />
                                )}
                            </div>
                        ))}

                        {!isImageTool && (
                            <div>
                                <label className="block text-xs font-bold uppercase tracking-widest mb-1.5">
                                    AI model
                                </label>
                                <ModelSelector value={modelCfg} onChange={setModelCfg} />
                            </div>
                        )}

                        {isImageTool && (
                            <div className="nb-border rounded-xl bg-[#FDE047] px-4 py-2.5 text-xs font-bold uppercase tracking-widest">
                                Powered by Nano Banana (Gemini)
                            </div>
                        )}
                    </div>

                    <div className="mt-6 flex gap-3">
                        <button
                            type="submit"
                            disabled={!canSubmit || loading}
                            data-testid="generate-btn"
                            className="inline-flex items-center gap-2 bg-[#A7F3D0] text-black nb-border rounded-xl px-6 py-3.5 font-black uppercase tracking-widest text-sm nb-shadow hover:-translate-y-1 hover:shadow-[6px_6px_0px_#0A0A0A] active:translate-y-1 active:translate-x-1 active:shadow-none transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:translate-y-0 disabled:shadow-[4px_4px_0px_#0A0A0A]"
                        >
                            {loading ? (
                                <Loader2 className="w-4 h-4 animate-spin" strokeWidth={3} />
                            ) : (
                                <Sparkles className="w-4 h-4" strokeWidth={3} />
                            )}
                            {loading ? (isImageTool ? "Painting…" : "Generating…") : "Generate"}
                        </button>
                        <button
                            type="button"
                            onClick={onReset}
                            data-testid="reset-btn"
                            className="inline-flex items-center gap-2 bg-white text-black nb-border rounded-xl px-4 py-3.5 font-bold text-sm nb-shadow-sm hover:-translate-y-[2px] hover:shadow-[4px_4px_0px_#0A0A0A] active:translate-y-[2px] active:translate-x-[2px] active:shadow-none transition-all"
                        >
                            <RotateCcw className="w-4 h-4" strokeWidth={3} />
                            Reset
                        </button>
                    </div>
                </form>

                {/* OUTPUT */}
                <div
                    className={`nb-border-4 rounded-3xl ${isImageTool ? "bg-white" : "bg-[#0A0A0A] text-[#FAFAFA]"} p-6 md:p-8 nb-shadow min-h-[420px] flex flex-col`}
                >
                    <div className="flex items-center justify-between mb-4">
                        <div
                            className={`font-display text-xl font-black flex items-center gap-2 ${isImageTool ? "" : ""}`}
                        >
                            <span className="bg-[#FDE047] text-black nb-border px-2 py-0.5 rounded-lg text-sm">
                                Output
                            </span>
                        </div>
                        {!isImageTool && (
                            <button
                                onClick={onCopy}
                                disabled={!output}
                                data-testid="copy-output-btn"
                                className="inline-flex items-center gap-2 bg-white text-black nb-border rounded-xl px-3 py-2 font-bold text-xs uppercase tracking-widest nb-shadow-sm hover:-translate-y-[2px] hover:shadow-[4px_4px_0px_#0A0A0A] active:translate-y-[2px] active:translate-x-[2px] active:shadow-none transition-all disabled:opacity-40"
                            >
                                {copied ? (
                                    <Check className="w-4 h-4" strokeWidth={3} />
                                ) : (
                                    <Copy className="w-4 h-4" strokeWidth={3} />
                                )}
                                {copied ? "Copied" : "Copy"}
                            </button>
                        )}
                    </div>

                    {isImageTool ? (
                        <div
                            data-testid="image-output"
                            className="flex-1 flex flex-col gap-4"
                        >
                            {loading && images.length === 0 && (
                                <div className="flex-1 flex items-center justify-center flex-col gap-3 text-neutral-500">
                                    <Loader2
                                        className="w-10 h-10 animate-spin"
                                        strokeWidth={2.5}
                                    />
                                    <div className="font-bold uppercase tracking-widest text-xs">
                                        Cooking pixels…
                                    </div>
                                </div>
                            )}
                            {!loading && images.length === 0 && (
                                <div className="flex-1 flex items-center justify-center flex-col gap-3 text-neutral-400">
                                    <ImageIcon className="w-10 h-10" strokeWidth={2.5} />
                                    <div className="font-bold uppercase tracking-widest text-xs text-center">
                                        Your generated image
                                        <br />
                                        will appear here.
                                    </div>
                                </div>
                            )}
                            {images.map((src, idx) => (
                                <div
                                    key={idx}
                                    className="relative nb-border rounded-2xl overflow-hidden bg-neutral-100"
                                >
                                    <img
                                        src={src}
                                        alt={`Generated ${idx + 1}`}
                                        className="w-full h-auto"
                                        data-testid={`output-image-${idx}`}
                                    />
                                    <button
                                        onClick={() => downloadImage(src, idx)}
                                        data-testid={`download-image-${idx}`}
                                        className="absolute top-3 right-3 inline-flex items-center gap-2 bg-white text-black nb-border rounded-xl px-3 py-2 font-bold text-xs uppercase tracking-widest nb-shadow-sm hover:-translate-y-[2px] hover:shadow-[4px_4px_0px_#0A0A0A] transition-all"
                                    >
                                        <Download className="w-4 h-4" strokeWidth={3} />
                                        Download
                                    </button>
                                </div>
                            ))}
                            {output && (
                                <div className="text-sm text-neutral-700 italic">
                                    {output}
                                </div>
                            )}
                        </div>
                    ) : (
                        <div
                            data-testid="output-textarea"
                            className="flex-1 font-mono text-sm leading-relaxed whitespace-pre-wrap overflow-auto"
                        >
                            {loading && !output && (
                                <div className="flex items-center gap-3 text-neutral-400">
                                    <div className="w-3 h-6 bg-[#FDE047]" />
                                    <span className="nb-cursor">Thinking…</span>
                                </div>
                            )}
                            {!loading && !output && (
                                <div className="text-neutral-500">
                                    Your generated {tool.name.toLowerCase()} output will appear
                                    here.
                                </div>
                            )}
                            {output && output}
                        </div>
                    )}
                </div>
            </div>

            {/* RELATED TOOLS — internal linking (visible right after input/output) */}
            <RelatedTools currentSlug={tool.slug} category={tool.category} />

            {/* SEO ARTICLE — auto-generated, cached */}
            <ToolArticle slug={tool.slug} toolName={tool.name} />
        </div>
    );
}
